import React, { useMemo } from 'react';

import { css } from '@emotion/css';
import {
  NgrokIntegrationDTO,
  TunnelHistorySnapshotDTO,
} from '@recurrency/core-api-schema/dist/integrations/tenantIntegrationDTO';
import { theme } from 'theme';

import { Tooltip } from 'components/Tooltip';
import { Typography } from 'components/Typography';

import { formatDateWithTimezone } from 'utils/formatting';

// Scrollable container with custom scrollbar
const scrollableContainerStyles = css`
  display: flex;
  align-items: center;
  overflow-x: auto;
  overflow-y: visible;
  max-width: 100%;
  padding-bottom: 8px;
  padding-top: 24px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.neutral[200]};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral[400]};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.neutral[500]};
  }
`;

const containerStyles = css`
  padding: 12px 16px;
  background: ${theme.colors.neutral[100]};
  margin-top: 8px;
  margin-bottom: 16px;
`;

const emptyStateContainerStyles = css`
  padding: 16px;
  background: ${theme.colors.neutral[100]};
  margin-top: 16px;
`;

const emptyStateTitleStyles = css`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const emptyStateMessageStyles = css`
  color: ${theme.colors.neutral[600]};
  font-size: 13px;
`;

const edgeIdLabelStyles = css`
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.neutral[800]};
  min-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`;

const spacerStyles = css`
  width: 32px;
  flex-shrink: 0;
`;

const squaresContainerStyles = css`
  display: flex;
  gap: 2px;
`;

const snapshotItemStyles = css`
  position: relative;
`;

const dateLabelStyles = css`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: ${theme.colors.neutral[600]};
  white-space: nowrap;
`;

const dateMarkerTickStyles = css`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 6px;
  background-color: ${theme.colors.neutral[400]};
`;

const statusSquareStyles = css`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  cursor: pointer;
`;

interface NgrokStatusHistoryProps {
  history: TunnelHistorySnapshotDTO[];
  agentInfo: NgrokIntegrationDTO;
}

interface ProcessedSnapshot extends TunnelHistorySnapshotDTO {
  color: string;
  hasChanges: boolean;
}

function detectAgentChanges(
  current: TunnelHistorySnapshotDTO,
  previous: TunnelHistorySnapshotDTO | undefined,
): boolean {
  // No previous snapshot or either is down - no changes to detect
  if (!previous || !current.activeTunnel || !previous.activeTunnel) {
    return false;
  }

  const curr = current.activeTunnel.tunnelSession;
  const prev = previous.activeTunnel.tunnelSession;

  return (
    curr.agentVersion !== prev.agentVersion || curr.os !== prev.os || curr.ip !== prev.ip || curr.region !== prev.region
  );
}

function getStatusColor(status: 'active' | 'down', hasChanges: boolean): string {
  if (status === 'down') {
    return theme.colors.danger[500];
  }

  if (hasChanges) {
    return theme.colors.warning[500];
  }

  return theme.colors.success[500];
}

function processHistory(history: TunnelHistorySnapshotDTO[]): ProcessedSnapshot[] {
  return history.map((snapshot, idx) => {
    const prevSnapshot = history[idx + 1]; // History is newest first
    const hasChanges = detectAgentChanges(snapshot, prevSnapshot);
    const color = getStatusColor(snapshot.status, hasChanges);

    return {
      ...snapshot,
      color,
      hasChanges,
    };
  });
}

function getTooltipContent(snapshot: ProcessedSnapshot): React.ReactNode {
  return (
    <div>
      <Typography type="small">
        <span style={{ fontWeight: 'bold' }}>Status:</span> {snapshot.status}
      </Typography>
      <Typography type="small">
        <span style={{ fontWeight: 'bold' }}>Time:</span> {formatDateWithTimezone(snapshot.collectedAt)}
      </Typography>
      {snapshot.activeTunnel && (
        <>
          <Typography type="small" style={{ marginTop: '8px' }}>
            <span style={{ fontWeight: 'bold' }}>Agent Version:</span>{' '}
            {snapshot.activeTunnel.tunnelSession.agentVersion}
          </Typography>
          <Typography type="small">
            <span style={{ fontWeight: 'bold' }}>OS:</span> {snapshot.activeTunnel.tunnelSession.os}
          </Typography>
          <Typography type="small">
            <span style={{ fontWeight: 'bold' }}>IP:</span> {snapshot.activeTunnel.tunnelSession.ip}
          </Typography>
          <Typography type="small">
            <span style={{ fontWeight: 'bold' }}>Region:</span> {snapshot.activeTunnel.tunnelSession.region}
          </Typography>
          <Typography type="small">
            <span style={{ fontWeight: 'bold' }}>Source:</span> {snapshot.activeTunnel.source}
          </Typography>
          {snapshot.hasChanges && (
            <Typography type="small" style={{ marginTop: '8px', color: theme.colors.warning[600], fontWeight: 'bold' }}>
              ⚠ Agent config changed
            </Typography>
          )}
        </>
      )}
    </div>
  );
}

export function NgrokStatusHistory({ history, agentInfo }: NgrokStatusHistoryProps) {
  const processedHistory = useMemo(() => processHistory(history), [history]);

  if (!history || history.length === 0) {
    return (
      <div className={emptyStateContainerStyles}>
        <div className={emptyStateTitleStyles}>Status History for {agentInfo.domain}</div>
        <p className={emptyStateMessageStyles}>No status history available</p>
      </div>
    );
  }

  return (
    <div className={containerStyles}>
      <div className={scrollableContainerStyles}>
        <Tooltip title={`Endpoint ID: ${agentInfo.edgeId}`} placement="top">
          <div className={edgeIdLabelStyles}>Agent Status:</div>
        </Tooltip>
        <div className={spacerStyles} />
        <div className={squaresContainerStyles}>
          {processedHistory.map((snapshot, idx) => {
            const showDateMarker = idx % 12 === 0;
            const dateLabel = formatDateWithTimezone(snapshot.collectedAt);

            return (
              <div key={snapshot.collectedAt} className={snapshotItemStyles}>
                {showDateMarker && (
                  <>
                    <div className={dateLabelStyles}>{dateLabel}</div>
                    <div className={dateMarkerTickStyles} />
                  </>
                )}
                <Tooltip
                  title={getTooltipContent(snapshot)}
                  placement="top"
                  overlayInnerStyle={{ backgroundColor: 'white', color: theme.colors.neutral[900] }}
                >
                  <div className={statusSquareStyles} style={{ backgroundColor: snapshot.color }} />
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
